// Phase 11 Step 9 — Owl Hour styles.
//
// Night-themed token-only CSSProperties for the Owl Hour Accumulator.
// Uses the --owl-* CSS custom properties defined in owl.css for
// night-palette colors, composed with elevation + motion helpers.
//
// Typography hierarchy:
//   Bebas Neue  — page title, tier labels, result headlines
//   JetBrains Mono — odds, confidence, timestamps, leg numbers
//   DM Sans / Inter — body text, reasoning, descriptions
//   Playfair Display — section headings, morning reveal greeting
//
// Trust contract:
//   - Token-only. No raw hex/rgb. All colors via var(--owl-*) or var(--sz-*).
//   - Elevation from elevation.ts. Motion from motion.ts.

import type { CSSProperties } from "react";
import { cardLow, cardMedium, cardLowSelected, cardHigh } from "../../styles/elevation.ts";
import { transitionFast, transitionStandard } from "../../styles/motion.ts";

// ═══════════════════════════════════════════════════════════════
// PAGE SHELL
// ═══════════════════════════════════════════════════════════════

export const owlRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-6)",
  maxWidth: 1480,
  margin: "0 auto",
  padding: "var(--sz-space-5) var(--sz-space-5) var(--sz-space-8)",
};

// ═══════════════════════════════════════════════════════════════
// HERO BANNER — Tonight's session window
// ═══════════════════════════════════════════════════════════════

export const heroCard: CSSProperties = {
  ...cardMedium,
  background: "var(--owl-elev)",
  borderRadius: "var(--sz-radius-xl)",
  padding: "var(--sz-space-6) var(--sz-space-7)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
  position: "relative",
  overflow: "hidden",
};

export const heroGlow: CSSProperties = {
  position: "absolute",
  top: "-60%",
  right: "-10%",
  width: "400px",
  height: "400px",
  borderRadius: "50%",
  background: "radial-gradient(circle, var(--owl-violet-soft) 0%, transparent 70%)",
  pointerEvents: "none",
};

export const heroTitleRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--sz-space-3)",
};

export const heroMoonIcon: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  background: "linear-gradient(135deg, var(--owl-violet), var(--owl-cyan))",
  display: "grid",
  placeItems: "center",
  fontSize: "20px",
  flexShrink: 0,
};

export const heroTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "var(--sz-display-sm)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--owl-text)",
  lineHeight: 1,
  margin: 0,
};

export const heroSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  color: "var(--owl-text-2)",
  lineHeight: 1.5,
};

export const heroStatsRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "var(--sz-space-3)",
  marginTop: "var(--sz-space-2)",
};

export const heroStat: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  padding: "var(--sz-space-3) var(--sz-space-4)",
  background: "var(--owl-elev-2)",
  borderRadius: "var(--sz-radius-lg)",
  borderLeft: "3px solid var(--owl-violet)",
};

export const heroStatValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-lg)",
  fontWeight: 600,
  color: "var(--owl-text)",
  letterSpacing: "0.02em",
};

export const heroStatLabel: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "11px",
  color: "var(--owl-text-3)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

// ═══════════════════════════════════════════════════════════════
// TIER SELECTOR — responsive five-tier grid
// ═══════════════════════════════════════════════════════════════

export const tierGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "var(--sz-space-3)",
};

export const tierTab: CSSProperties = {
  ...cardLow,
  ...transitionFast,
  background: "var(--owl-elev)",
  borderRadius: "var(--sz-radius-lg)",
  padding: "var(--sz-space-4) var(--sz-space-4) var(--sz-space-3)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  cursor: "pointer",
  borderTop: "3px solid transparent",
  position: "relative",
  overflow: "hidden",
};

export const tierTabActive: CSSProperties = {
  ...cardLowSelected,
  background: "var(--owl-elev-2)",
  borderTop: "3px solid var(--owl-violet)",
};

export const tierLabel: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "20px",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--owl-text)",
  lineHeight: 1.1,
};

export const tierSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--owl-text-3)",
  lineHeight: 1.4,
};

export const tierOdds: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-lg)",
  fontWeight: 700,
  color: "var(--owl-violet)",
  letterSpacing: "0.03em",
};

export const tierLegs: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--owl-text-3)",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

export const tierTonight: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--owl-cyan)",
  fontWeight: 600,
  marginTop: "auto",
  paddingTop: "var(--sz-space-2)",
  borderTop: "1px solid var(--owl-border-faint)",
};

// ═══════════════════════════════════════════════════════════════
// SIGNAL CARD — Slip display
// ═══════════════════════════════════════════════════════════════

export const slipCard: CSSProperties = {
  ...cardMedium,
  background: "var(--owl-elev)",
  borderRadius: "var(--sz-radius-xl)",
  overflow: "hidden",
};

export const slipHeader: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--sz-space-5) var(--sz-space-5) var(--sz-space-3)",
  borderBottom: "1px solid var(--owl-border-faint)",
};

export const slipHeaderLeft: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-1)",
};

export const slipTierName: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "28px",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--owl-text)",
  lineHeight: 1,
};

export const slipDate: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--owl-text-3)",
};

export const slipHeaderRight: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "2px",
};

export const slipOddsBig: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "32px",
  fontWeight: 700,
  color: "var(--owl-violet)",
  lineHeight: 1,
  letterSpacing: "-0.02em",
};

export const slipOddsLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--owl-text-3)",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

export const slipConfidence: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--owl-cyan)",
  letterSpacing: "0.02em",
};

export const slipBody: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 0,
};

export const slipFooter: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--sz-space-4) var(--sz-space-5)",
  borderTop: "1px solid var(--owl-border-faint)",
  background: "var(--owl-elev-2)",
};

export const slipFooterStat: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "1px",
};

export const slipFooterValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-md)",
  fontWeight: 600,
  color: "var(--owl-text)",
};

export const slipFooterLabel: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "10px",
  color: "var(--owl-text-3)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

// ═══════════════════════════════════════════════════════════════
// LEG ROW — Per-leg display inside slip card
// ═══════════════════════════════════════════════════════════════

export const legRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "36px 1fr auto",
  gap: "var(--sz-space-3)",
  padding: "var(--sz-space-4) var(--sz-space-5)",
  borderBottom: "1px solid var(--owl-border-faint)",
  alignItems: "start",
  ...transitionFast,
};

export const legRowLast: CSSProperties = {
  borderBottom: "none",
};

export const legNumber: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "24px",
  color: "var(--owl-violet)",
  lineHeight: 1,
  textAlign: "center",
  paddingTop: "2px",
};

export const legContent: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-1)",
  minWidth: 0,
};

export const legMatch: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  fontWeight: 600,
  color: "var(--owl-text)",
  lineHeight: 1.3,
};

export const legLeague: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--owl-text-3)",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

export const legPick: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  fontWeight: 500,
  color: "var(--owl-cyan)",
  marginTop: "var(--sz-space-1)",
};

// ─── OWL UX clarity fix (2026-05-27) ──────────────────────────
// New leg hierarchy: PICK · {Team} (big/gold/primary), vs {Opponent}
// (muted, smaller), then league + favorite tag.

export const legPickPrimary: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-lg)",
  fontWeight: 600,
  color: "var(--owl-cyan)",
  lineHeight: 1.3,
  letterSpacing: "0.01em",
};

export const legOpponent: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--owl-text-3)",
  lineHeight: 1.3,
  marginTop: "2px",
};

export const legFavoriteTag: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--owl-violet)",
};

export const legReasoning: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--owl-text-2)",
  lineHeight: 1.55,
  fontStyle: "italic",
  marginTop: "var(--sz-space-1)",
};

export const legKickoff: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--owl-text-3)",
  marginTop: "var(--sz-space-1)",
};

export const legRight: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "var(--sz-space-1)",
  flexShrink: 0,
};

export const legOdds: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-lg)",
  fontWeight: 700,
  color: "var(--owl-text)",
  lineHeight: 1,
};

export const legConfidence: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--owl-cyan)",
  letterSpacing: "0.04em",
};

// ═══════════════════════════════════════════════════════════════
// FLAG BADGES — Altitude, rivalry, travel, sample, turf
// ═══════════════════════════════════════════════════════════════

export const flagRow: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-1)",
  flexWrap: "wrap",
  marginTop: "var(--sz-space-1)",
};

export const flagBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  padding: "2px 6px",
  borderRadius: "var(--sz-radius-sm)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  lineHeight: 1.3,
  whiteSpace: "nowrap",
};

export const flagAltitude: CSSProperties = {
  ...flagBadge,
  background: "var(--owl-cyan-soft)",
  color: "var(--owl-cyan)",
};

export const flagRivalry: CSSProperties = {
  ...flagBadge,
  background: "var(--owl-violet-soft)",
  color: "var(--owl-violet)",
};

export const flagTravel: CSSProperties = {
  ...flagBadge,
  background: "var(--owl-elev-2)",
  color: "var(--owl-text-2)",
};

export const flagSample: CSSProperties = {
  ...flagBadge,
  background: "var(--sz-amber-dim)",
  color: "var(--sz-amber)",
};

export const flagTurf: CSSProperties = {
  ...flagBadge,
  background: "var(--owl-elev-2)",
  color: "var(--owl-cyan)",
};

export const flagHomeAdv: CSSProperties = {
  ...flagBadge,
  background: "var(--owl-elev-2)",
  color: "var(--owl-text-2)",
};

// ═══════════════════════════════════════════════════════════════
// MORNING REVEAL — Result screen
// ═══════════════════════════════════════════════════════════════

export const revealCard: CSSProperties = {
  ...cardHigh,
  background: "var(--owl-elev)",
  borderRadius: "var(--sz-radius-xl)",
  overflow: "hidden",
};

export const revealHeader: CSSProperties = {
  padding: "var(--sz-space-7) var(--sz-space-6) var(--sz-space-5)",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "var(--sz-space-3)",
};

export const revealGreeting: CSSProperties = {
  fontFamily: "var(--sz-font-head)",
  fontSize: "18px",
  fontWeight: 400,
  color: "var(--owl-text-2)",
  fontStyle: "italic",
  letterSpacing: "0.02em",
};

export const revealResultBig: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(48px, 8vw, 72px)",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  lineHeight: 1,
};

export const revealResultWin: CSSProperties = {
  ...revealResultBig,
  color: "var(--owl-cyan)",
};

export const revealResultLoss: CSSProperties = {
  ...revealResultBig,
  color: "var(--owl-text-3)",
};

export const revealResultPartial: CSSProperties = {
  ...revealResultBig,
  color: "var(--owl-violet)",
};

export const revealReturnAmount: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "clamp(28px, 5vw, 44px)",
  fontWeight: 700,
  color: "var(--owl-text)",
  letterSpacing: "-0.01em",
  lineHeight: 1,
};

export const revealStake: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-md)",
  color: "var(--owl-text-3)",
};

export const revealOdds: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-md)",
  color: "var(--owl-violet)",
  fontWeight: 600,
};

export const revealBody: CSSProperties = {
  padding: "0 var(--sz-space-5) var(--sz-space-5)",
  display: "flex",
  flexDirection: "column",
  gap: 0,
};

export const revealSectionTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "16px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--owl-text-3)",
  padding: "var(--sz-space-3) 0 var(--sz-space-2)",
};

export const revealLegRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "28px 1fr auto",
  gap: "var(--sz-space-3)",
  padding: "var(--sz-space-3) 0",
  borderBottom: "1px solid var(--owl-border-faint)",
  alignItems: "center",
};

export const revealLegIcon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "16px",
  textAlign: "center",
  lineHeight: 1,
};

export const revealLegWin: CSSProperties = {
  color: "var(--owl-cyan)",
};

export const revealLegLoss: CSSProperties = {
  color: "var(--owl-text-3)",
  opacity: 0.7,
};

export const revealLegPending: CSSProperties = {
  color: "var(--owl-violet)",
};

export const revealLegMatch: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--owl-text)",
  lineHeight: 1.4,
};

export const revealLegDetail: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "12px",
  color: "var(--owl-text-3)",
  lineHeight: 1.4,
};

export const revealLegResult: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

export const revealFooter: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-3)",
  padding: "var(--sz-space-5) var(--sz-space-5)",
  borderTop: "1px solid var(--owl-border-faint)",
  background: "var(--owl-elev-2)",
};

// ═══════════════════════════════════════════════════════════════
// SHARE CARD — Branded social sharing
// ═══════════════════════════════════════════════════════════════

export const shareCard: CSSProperties = {
  ...cardHigh,
  background: "linear-gradient(160deg, var(--owl-elev) 0%, var(--owl-inset) 100%)",
  borderRadius: "var(--sz-radius-xl)",
  padding: "var(--sz-space-7) var(--sz-space-6)",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "var(--sz-space-4)",
  maxWidth: 420,
  margin: "0 auto",
};

export const shareBrand: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "14px",
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "var(--owl-text-3)",
};

export const shareAmount: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(40px, 7vw, 64px)",
  letterSpacing: "0.04em",
  color: "var(--owl-cyan)",
  lineHeight: 1,
};

export const shareTier: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--owl-violet)",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

// ═══════════════════════════════════════════════════════════════
// HISTORY TABLE — Recent owl results
// ═══════════════════════════════════════════════════════════════

export const historySection: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
};

export const historySectionTitle: CSSProperties = {
  fontFamily: "var(--sz-font-head)",
  fontSize: "18px",
  color: "var(--owl-text)",
  fontWeight: 400,
  fontStyle: "italic",
  letterSpacing: "0.01em",
};

export const historyTable: CSSProperties = {
  ...cardLow,
  background: "var(--owl-elev)",
  borderRadius: "var(--sz-radius-lg)",
  overflow: "hidden",
};

export const historyHeader: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "100px 1fr 80px 80px 100px",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-2) var(--sz-space-4)",
  background: "var(--owl-elev-2)",
  borderBottom: "1px solid var(--owl-border-faint)",
};

export const historyHeaderCell: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  color: "var(--owl-text-3)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontWeight: 500,
};

export const historyRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "100px 1fr 80px 80px 100px",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-3) var(--sz-space-4)",
  borderBottom: "1px solid var(--owl-border-faint)",
  alignItems: "center",
  ...transitionFast,
};

export const historyDate: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--owl-text-2)",
};

export const historyTier: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "14px",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--owl-text)",
};

export const historyLegs: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--owl-text-2)",
  textAlign: "center",
};

export const historyResult: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  textAlign: "center",
};

export const historyReturn: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-md)",
  fontWeight: 600,
  color: "var(--owl-text)",
  textAlign: "right",
};

// ═══════════════════════════════════════════════════════════════
// SUBSCRIPTION GATE
// ═══════════════════════════════════════════════════════════════

export const subGate: CSSProperties = {
  ...cardHigh,
  background: "var(--owl-elev)",
  borderRadius: "var(--sz-radius-xl)",
  padding: "var(--sz-space-8) var(--sz-space-6)",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "var(--sz-space-4)",
  maxWidth: 520,
  margin: "var(--sz-space-8) auto",
};

export const subGateTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "var(--sz-display-sm)",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--owl-text)",
  lineHeight: 1.1,
};

export const subGateDesc: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  color: "var(--owl-text-2)",
  lineHeight: 1.6,
  maxWidth: 400,
};

export const subGatePrice: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "28px",
  fontWeight: 700,
  color: "var(--owl-violet)",
  lineHeight: 1,
};

export const subGatePriceSub: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  color: "var(--owl-text-3)",
};

export const subGateCta: CSSProperties = {
  ...transitionStandard,
  fontFamily: "var(--sz-font-display)",
  fontSize: "16px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  padding: "var(--sz-space-3) var(--sz-space-7)",
  borderRadius: "var(--sz-radius-lg)",
  background: "linear-gradient(135deg, var(--owl-violet), var(--owl-cyan))",
  color: "var(--sz-text-inverse)",
  border: "none",
  cursor: "pointer",
};

export const subGateTrial: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--owl-text-3)",
};

// ═══════════════════════════════════════════════════════════════
// BUTTONS — Owl-specific CTAs
// ═══════════════════════════════════════════════════════════════

export const primaryBtn: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-display)",
  fontSize: "14px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  padding: "var(--sz-space-2) var(--sz-space-5)",
  borderRadius: "var(--sz-radius-lg)",
  background: "var(--owl-violet)",
  color: "var(--sz-text-inverse)",
  border: "none",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

export const secondaryBtn: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-display)",
  fontSize: "14px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  padding: "var(--sz-space-2) var(--sz-space-5)",
  borderRadius: "var(--sz-radius-lg)",
  background: "var(--owl-elev-2)",
  color: "var(--owl-text)",
  border: "1px solid var(--owl-border)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

export const shareBtn: CSSProperties = {
  ...transitionStandard,
  fontFamily: "var(--sz-font-display)",
  fontSize: "15px",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  padding: "var(--sz-space-3) var(--sz-space-6)",
  borderRadius: "var(--sz-radius-lg)",
  background: "linear-gradient(135deg, var(--owl-violet), var(--owl-cyan))",
  color: "var(--sz-text-inverse)",
  border: "none",
  cursor: "pointer",
  flex: 1,
};

// ═══════════════════════════════════════════════════════════════
// EMPTY STATE + DISCLOSURE
// ═══════════════════════════════════════════════════════════════

export const emptyState: CSSProperties = {
  ...cardLow,
  background: "var(--owl-elev)",
  borderRadius: "var(--sz-radius-xl)",
  padding: "var(--sz-space-8) var(--sz-space-6)",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "var(--sz-space-3)",
};

export const emptyIcon: CSSProperties = {
  fontSize: "48px",
  lineHeight: 1,
  opacity: 0.5,
};

export const emptyTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "22px",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--owl-text)",
};

export const emptyDesc: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--owl-text-3)",
  maxWidth: 360,
  lineHeight: 1.6,
};

export const phantomBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--owl-text-3)",
  textAlign: "center",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  padding: "var(--sz-space-2) 0",
};

export const disclosureBanner: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "12px",
  color: "var(--owl-text-3)",
  lineHeight: 1.6,
  padding: "var(--sz-space-3) var(--sz-space-4)",
  background: "var(--owl-elev-2)",
  borderRadius: "var(--sz-radius-lg)",
  borderLeft: "3px solid var(--owl-violet-soft)",
};

// ═══════════════════════════════════════════════════════════════
// SECTION DIVIDERS — Typographic rhythm
// ═══════════════════════════════════════════════════════════════

export const sectionLabel: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "14px",
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  color: "var(--owl-text-3)",
  paddingBottom: "var(--sz-space-2)",
  borderBottom: "1px solid var(--owl-border-faint)",
};
